import { useEffect, useMemo, useState } from "react";
import AppLayout from "@/components/AppLayout";
import api, { API, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ClipboardList, Download, Calendar, Search, Users } from "lucide-react";
import ImageUploader from "@/components/ImageUploader";
import { Trash2, Plus } from "lucide-react";

function todayISO() { return new Date().toISOString().slice(0, 10); }
function plusDays(iso, n) { const d = new Date(iso); d.setDate(d.getDate() + n); return d.toISOString().slice(0, 10); }
function fmtDe(iso) { if (!iso) return "—"; const d = new Date(iso); return d.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit" }); }

export default function OpsPlannerPage() {
    const [from, setFrom] = useState(todayISO());
    const [to, setTo] = useState(plusDays(todayISO(), 13));
    const [q, setQ] = useState("");
    const [data, setData] = useState({ days: [], trips: [] });
    const [loading, setLoading] = useState(false);
    const [mode, setMode] = useState("compact"); // compact | week
    const [pageSize, setPageSize] = useState("A3"); // A3 | A4
    const [briefingDate, setBriefingDate] = useState(todayISO());
    const [editingTrip, setEditingTrip] = useState(null); // {id, label, phone, passengers}

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/ops/planner", { params: { from, to, q: q || undefined } });
            setData(data);
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, [from, to]);

    const saveNote = async (tripId, dateIso, note) => {
        try {
            const existing = { ...(await api.get(`/trips/${tripId}`)).data.ops_notes || {} };
            existing[dateIso] = note;
            await api.put(`/trips/${tripId}`, { ops_notes: existing });
            setData({ ...data, trips: data.trips.map((t) => t.trip_id === tripId
                ? { ...t, cells: { ...t.cells, [dateIso]: { ...t.cells[dateIso], manual: note } } }
                : t) });
        } catch (err) { toast.error(formatApiError(err)); }
    };

    const openEditor = async (tripId) => {
        try {
            const { data } = await api.get(`/trips/${tripId}`);
            setEditingTrip({
                id: tripId,
                ops_client_label: data.ops_client_label || data.client_name || "",
                client_phone: data.client_phone || "",
                passengers: data.passengers || [],
            });
        } catch (err) { toast.error(formatApiError(err)); }
    };

    const savePassengersModal = async (patch) => {
        if (!editingTrip) return;
        try {
            await api.put(`/trips/${editingTrip.id}`, patch);
            setEditingTrip({ ...editingTrip, ...patch });
            await load();
            toast.success("Saved");
        } catch (err) { toast.error(formatApiError(err)); }
    };

    const downloadPdf = async (type) => {
        try {
            const url = type === "briefing"
                ? `${API}/ops/briefing/pdf?date=${briefingDate}`
                : `${API}/ops/planner/pdf?from=${from}&to=${to}&mode=${mode}&page_size=${pageSize}${q ? `&q=${encodeURIComponent(q)}` : ""}`;
            const res = await fetch(url, { credentials: "include" });
            if (!res.ok) throw new Error("PDF-Download fehlgeschlagen");
            const blob = await res.blob();
            const objectUrl = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = objectUrl;
            a.download = type === "briefing" ? `Ops-Daily_${briefingDate}.pdf` : `Ops-Planer_${from}_${to}.pdf`;
            document.body.appendChild(a); a.click(); document.body.removeChild(a);
            URL.revokeObjectURL(objectUrl);
        } catch (err) { toast.error(String(err)); }
    };

    const filteredTrips = useMemo(() => {
        if (!q.trim()) return data.trips;
        const needle = q.toLowerCase();
        return data.trips.filter((r) =>
            (r.client_name || "").toLowerCase().includes(needle) ||
            (r.destination || "").toLowerCase().includes(needle) ||
            (r.passengers || "").toLowerCase().includes(needle)
        );
    }, [data.trips, q]);

    return (
        <AppLayout>
            <div className="max-w-full">
                <div className="mb-5">
                    <p className="text-xs uppercase tracking-[0.18em] text-ink-tertiary mb-2">Operations</p>
                    <h1 className="text-3xl font-semibold text-ink mb-1">Ops Planner</h1>
                    <p className="text-sm text-ink-tertiary">Team overview for local staff · Clients × Days · auto-populated from trip data</p>
                </div>

                <div className="bg-white border border-sand-300 rounded-md p-4 mb-4 flex flex-wrap items-end gap-3">
                    <div>
                        <label className="block text-[10px] font-bold uppercase tracking-[0.14em] text-ink-secondary mb-1">From</label>
                        <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="ops-from"
                            className="bg-white border border-sand-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-navy" />
                    </div>
                    <div>
                        <label className="block text-[10px] font-bold uppercase tracking-[0.14em] text-ink-secondary mb-1">To</label>
                        <input type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid="ops-to"
                            className="bg-white border border-sand-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-navy" />
                    </div>
                    <div className="flex-1 min-w-[200px] relative">
                        <label className="block text-[10px] font-bold uppercase tracking-[0.14em] text-ink-secondary mb-1">Search</label>
                        <Search className="w-4 h-4 absolute left-3 top-[34px] text-ink-tertiary pointer-events-none" />
                        <input type="text" value={q} onChange={(e) => setQ(e.target.value)} data-testid="ops-search"
                            placeholder="Client, location, passenger …"
                            className="w-full bg-white border border-sand-300 rounded-md pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-navy" />
                    </div>
                    <div>
                        <label className="block text-[10px] font-bold uppercase tracking-[0.14em] text-ink-secondary mb-1">Page Size</label>
                        <select value={pageSize} onChange={(e) => setPageSize(e.target.value)} data-testid="ops-pagesize"
                            className="bg-white border border-sand-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-navy">
                            <option value="A3">A3 (Landscape)</option>
                            <option value="A4">A4 (Landscape)</option>
                        </select>
                    </div>
                    <button onClick={() => downloadPdf("planner")} data-testid="ops-pdf"
                        className="inline-flex items-center gap-2 bg-ink text-white rounded-md px-4 py-2 text-sm font-medium hover:bg-navy transition-colors">
                        <Download className="w-4 h-4" /> Weekly PDF
                    </button>
                </div>

                <div className="bg-white border border-sand-300 rounded-md p-4 mb-6 flex flex-wrap items-end gap-3">
                    <Calendar className="w-4 h-4 text-ink-tertiary mb-2" />
                    <div>
                        <label className="block text-[10px] font-bold uppercase tracking-[0.14em] text-ink-secondary mb-1">Daily briefing for</label>
                        <input type="date" value={briefingDate} onChange={(e) => setBriefingDate(e.target.value)} data-testid="ops-briefing-date"
                            className="bg-white border border-sand-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-navy" />
                    </div>
                    <button onClick={() => downloadPdf("briefing")} data-testid="ops-briefing-pdf"
                        className="inline-flex items-center gap-2 bg-terracotta text-white rounded-md px-4 py-2 text-sm font-medium hover:bg-terracotta-dark transition-colors">
                        <Download className="w-4 h-4" /> Daily Ops Sheet
                    </button>
                    <p className="text-xs text-ink-tertiary ml-2">One page per day for the local team — full worksheet with signature line.</p>
                </div>

                {loading ? (
                    <p className="text-sm text-ink-tertiary">Loading …</p>
                ) : filteredTrips.length === 0 ? (
                    <div className="bg-white border border-sand-300 rounded-md p-10 text-center">
                        <ClipboardList className="w-10 h-10 text-ink-tertiary mx-auto mb-3" />
                        <p className="text-ink-secondary">No trips in the selected period.</p>
                    </div>
                ) : (
                    <div className="bg-white border border-sand-300 rounded-md overflow-auto">
                        <table className="text-xs border-collapse min-w-max">
                            <thead>
                                <tr className="bg-ink text-white">
                                    <th className="sticky left-0 bg-ink z-10 px-2 py-2 text-left font-bold uppercase tracking-[0.14em] text-[9px] min-w-[180px]">Client / Passengers</th>
                                    <th className="sticky left-[180px] bg-ink z-10 px-2 py-2 text-left font-bold uppercase tracking-[0.14em] text-[9px] min-w-[120px]">Phone</th>
                                    {data.days.map((d) => (
                                        <th key={d} className="px-2 py-2 text-center font-bold uppercase tracking-[0.14em] text-[9px] min-w-[140px]">
                                            {fmtDe(d)}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {filteredTrips.map((row, ri) => (
                                    <tr key={row.trip_id} className={ri % 2 === 0 ? "bg-white" : "bg-sand-100/50"}>
                                        <td className="sticky left-0 bg-inherit px-2 py-2 border border-sand-300 align-top min-w-[180px]">
                                            <div className="flex items-start justify-between gap-1">
                                                <div className="font-semibold text-ink flex-1">{row.client_name}</div>
                                                <button type="button" onClick={() => openEditor(row.trip_id)} data-testid={`ops-edit-${row.trip_id}`}
                                                    title="Edit client name & passengers"
                                                    className="p-1 rounded hover:bg-sand-200 text-ink-tertiary hover:text-navy transition-colors">
                                                    <Users className="w-3.5 h-3.5" />
                                                </button>
                                            </div>
                                            <div className="text-[10px] text-ink-tertiary whitespace-pre-line mt-0.5">{row.passengers}</div>
                                        </td>
                                        <td className="sticky left-[180px] bg-inherit px-2 py-2 border border-sand-300 align-top min-w-[120px] whitespace-pre-line text-ink-secondary">
                                            {row.client_phone || "—"}
                                        </td>
                                        {data.days.map((d) => {
                                            const cell = row.cells[d] || { auto: [], manual: "" };
                                            return (
                                                <td key={d} className="px-2 py-1 border border-sand-300 align-top min-w-[140px]">
                                                    {cell.auto.map((ln, i) => (
                                                        <div key={i} className="text-[10px] leading-tight text-ink mb-0.5">{ln.text}</div>
                                                    ))}
                                                    <textarea
                                                        rows={2}
                                                        defaultValue={cell.manual || ""}
                                                        onBlur={(e) => { if (e.target.value !== cell.manual) saveNote(row.trip_id, d, e.target.value); }}
                                                        placeholder="Note …"
                                                        data-testid={`ops-note-${row.trip_id}-${d}`}
                                                        className="w-full mt-1 text-[10px] leading-tight bg-transparent border border-dashed border-sand-300 rounded px-1 py-0.5 hover:border-navy focus:outline-none focus:ring-1 focus:ring-navy focus:bg-white transition-colors resize-none"
                                                    />
                                                </td>
                                            );
                                        })}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
                {editingTrip && (
                    <PassengerEditorModal
                        trip={editingTrip}
                        onClose={() => setEditingTrip(null)}
                        onSave={savePassengersModal}
                    />
                )}
            </div>
        </AppLayout>
    );
}

function PassengerEditorModal({ trip, onClose, onSave }) {
    const [label, setLabel] = useState(trip.ops_client_label || "");
    const [phone, setPhone] = useState(trip.client_phone || "");
    const [passengers, setPassengers] = useState(trip.passengers || []);
    const [saving, setSaving] = useState(false);

    const updatePax = (idx, patch) => setPassengers(passengers.map((p, i) => i === idx ? { ...p, ...patch } : p));
    const addPax = () => setPassengers([...passengers, { name: "", passport_image_url: "", notes: "" }]);
    const removePax = (idx) => setPassengers(passengers.filter((_, i) => i !== idx));

    const handleSave = async () => {
        setSaving(true);
        await onSave({ ops_client_label: label, client_phone: phone, passengers });
        setSaving(false);
        onClose();
    };

    return (
        <div className="fixed inset-0 z-50 bg-ink/60 flex items-center justify-center p-4" onClick={onClose}>
            <div className="bg-sand-100 max-w-2xl w-full max-h-[90vh] overflow-y-auto rounded-md shadow-xl" onClick={(e) => e.stopPropagation()} data-testid="passenger-editor-modal">
                <div className="px-6 py-4 border-b border-sand-300 flex items-center justify-between sticky top-0 bg-sand-100 z-10">
                    <h2 className="text-lg font-semibold text-ink">Edit Client & Passengers</h2>
                    <button type="button" onClick={onClose} className="text-ink-secondary hover:text-ink text-sm">✕</button>
                </div>
                <div className="p-6 space-y-4">
                    <div>
                        <label className="block text-[10px] font-bold uppercase tracking-[0.14em] text-ink-secondary mb-1">Client Label</label>
                        <input type="text" value={label} onChange={(e) => setLabel(e.target.value)} data-testid="modal-client-label"
                            placeholder="e.g. Lutz Family"
                            className="w-full bg-white border border-sand-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-navy" />
                    </div>
                    <div>
                        <label className="block text-[10px] font-bold uppercase tracking-[0.14em] text-ink-secondary mb-1">Phone</label>
                        <input type="text" value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="modal-client-phone"
                            placeholder="+41 79 …"
                            className="w-full bg-white border border-sand-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-navy" />
                    </div>
                    <div className="pt-3 border-t border-sand-300">
                        <div className="flex items-center justify-between mb-3">
                            <p className="text-xs font-semibold uppercase tracking-[0.14em] text-ink-tertiary">Passengers & Passport Photos</p>
                            <button type="button" onClick={addPax} data-testid="modal-add-passenger"
                                className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.14em] text-navy hover:text-navy-hover border border-navy/30 hover:border-navy rounded px-2 py-1 transition-colors">
                                <Plus className="w-3.5 h-3.5" /> Passenger
                            </button>
                        </div>
                        {passengers.length === 0 && (
                            <p className="text-xs text-ink-tertiary italic">No passengers yet. Click "Passenger" to add one.</p>
                        )}
                        <div className="space-y-3">
                            {passengers.map((p, idx) => (
                                <div key={idx} className="bg-white border border-sand-300 rounded-md p-3" data-testid={`modal-passenger-${idx}`}>
                                    <div className="flex items-start gap-3">
                                        <div className="flex-shrink-0 w-24">
                                            <ImageUploader
                                                label="Passport"
                                                value={p.passport_image_url || ""}
                                                onChange={(v) => updatePax(idx, { passport_image_url: v })}
                                                testid={`modal-passenger-${idx}-passport`}
                                            />
                                        </div>
                                        <div className="flex-1 space-y-2">
                                            <input type="text" value={p.name || ""} onChange={(e) => updatePax(idx, { name: e.target.value })}
                                                placeholder="Name (as in passport)"
                                                data-testid={`modal-passenger-${idx}-name`}
                                                className="w-full bg-white border border-sand-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-navy" />
                                            <input type="text" value={p.notes || ""} onChange={(e) => updatePax(idx, { notes: e.target.value })}
                                                placeholder="Notes (allergies, language, passport no. …)"
                                                data-testid={`modal-passenger-${idx}-notes`}
                                                className="w-full bg-white border border-sand-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-navy" />
                                        </div>
                                        <button type="button" onClick={() => removePax(idx)} data-testid={`modal-passenger-${idx}-remove`}
                                            className="p-1.5 rounded hover:bg-terracotta/10 text-ink-secondary hover:text-terracotta transition-colors">
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
                <div className="px-6 py-4 border-t border-sand-300 flex justify-end gap-2 sticky bottom-0 bg-sand-100">
                    <button type="button" onClick={onClose} className="text-sm text-ink-secondary hover:text-ink px-4 py-2">Cancel</button>
                    <button type="button" onClick={handleSave} disabled={saving} data-testid="modal-save"
                        className="bg-terracotta text-white rounded-md px-5 py-2 text-sm font-medium hover:bg-terracotta-dark disabled:opacity-50 transition-colors">
                        {saving ? "Saving …" : "Save"}
                    </button>
                </div>
            </div>
        </div>
    );
}
